import React from "react";
import { CourseDocumentApi } from "api";
import { DeleteButton, EditButton } from "components/atoms/Buttons";
import { PaziaBadge, PaziaButton } from "components/atoms";
import { ToggleReadinessButton } from "components/molecules";
import { useCourse } from "api/hooks";
import { coursesStatusApi } from "api/coursesStatusApi";

interface CourseDocumentListProps {
  documents: CourseDocumentApi[];
  courseId: number;
  onDelete: (courseDocumentId: number) => Promise<void>;
  onNavigateToCreate?: () => void;
  /** Starts a Documents 2.0 document. The button is shown only when the tenant switched Documents 2.0 on. */
  onNavigateToCreateV2?: () => void;
  documentsV2Enabled?: boolean;
  /** `version` tells the caller which form the document opens in. */
  onNavigateToEdit?: (documentId: number, version: 1 | 2) => void;
}
export const CourseDocumentList = ({
  documents,
  courseId,
  onDelete,
  onNavigateToCreate,
  onNavigateToCreateV2,
  documentsV2Enabled = false,
  onNavigateToEdit,
}: CourseDocumentListProps): JSX.Element | null => {
  const { loading: loadingCourse, data: course, invalidate } = useCourse(courseId);

  if (!course) return null;
  return (
    <div className="space-y-6">
      {/* Document Status Section */}
      <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-slate-700 dark:text-slate-300">Status zatwierdzenia dokumentów</div>
          <ToggleReadinessButton
            isReady={Boolean(course?.data.documentsReady)}
            onReady={coursesStatusApi.DOCUMENTS_READY}
            onUnready={coursesStatusApi.DOCUMENTS_UNREADY}
            courseId={course.data.id}
            readyText="Dokumenty zostały zatwierdzone"
            invalidate={invalidate}
          />
        </div>
      </div>

      {/* Documents Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Szablon dokumentów ({documents.length})
          </h3>
          <div className="flex items-center gap-2">
            <PaziaButton title="Dodaj nowy dokument" type="success" onClick={() => onNavigateToCreate?.()} />
            {documentsV2Enabled && (
              <PaziaButton title="Dodaj dokument 2.0" type="primary" onClick={() => onNavigateToCreateV2?.()} />
            )}
          </div>
        </div>

        {documents.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
              <svg className="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">Brak dokumentów</h4>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Dodaj pierwszy szablon dokumentu, aby rozpocząć.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((document) => (
              <div
                key={document.id}
                className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                        {document.name}
                        {document.version === 2 && (
                          <span title="Dokument 2.0" data-testid="document-v2-badge">
                            <PaziaBadge color="indigo" size="xs" className="shrink-0">
                              2.0
                            </PaziaBadge>
                          </span>
                        )}
                      </h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        ID: {document.id}
                        {document.version === 2 && document.v2 && <> • Szablon: {document.v2.templateName}</>}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 opacity-60 transition-opacity group-hover:opacity-100">
                    <EditButton onClick={() => onNavigateToEdit?.(document.id, document.version === 2 ? 2 : 1)} />
                    <DeleteButton onClick={() => onDelete(document.id)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
